import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DndContext, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LuBookOpen, LuFileEdit, LuFolder, LuFolderPlus, LuFolderSearch, LuShare2, LuTrash2, LuMoreVertical, LuFileText, LuAlertCircle, LuCheckCircle2, LuClock, LuArchive, LuDownload, LuPin, LuPinOff, LuLock, LuUnlock, LuInfo, LuCheck, LuCalendarClock, LuListChecks, LuUploadCloud, LuMail, LuPhoneIncoming, LuLandmark, LuSlidersHorizontal } from 'react-icons/lu';
import { IoClose } from 'react-icons/io5';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Cards from '../components/fragments/Cards';
import Breadcrumbs from '../components/Breadcrumbs';
import DossierToolbar from '../components/DossierToolbar';
import ShareFolderModal from '../components/ShareFolderModal';
import InfoDossierModal from '../components/InfoDossierModal';
import ArchiverDocumentModal from '../components/ArchiverDocumentModal';
import BulkFolderActionBar from '../components/BulkFolderActionBar';
import { usePermissions } from '../hooks/usePermissions';
import { createCategorie, deleteCategorieById, downloadCategorie, favoriCategorie, defavoriCategorie, verrouillerCategorie, deverrouillerCategorie, getCategorie, updateCatgory } from '../api/routes/categorie';
import { getDocument, getDocumentsATraiter, getCourrierCompteurs, rechercheDocuments } from '../api/routes/document';
import { getAppelsCompteurs } from '../api/routes/appel';
import { getChequesCompteurs } from '../api/routes/cheque';
import { getPaiCompteurs } from '../api/routes/pai';
import { getCompteursFluctuationsRh } from '../api/routes/fluctuationRh';
import { useOrdrePersonnalise } from '../hooks/useOrdrePersonnalise';
import { getFileTypeVisual } from '../utils/fileTypeIcons';
import { getDisplayName } from '../utils/common';
import { filtrerParRequete } from '../utils/recherche';
import { toneDossier } from '../utils/statutGroupe';
import { DENSITE_HAUTEUR, DENSITE_COLS } from '../utils/densite';
import { nomCategorie } from '../utils/libelleLocalise';
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuShortcut } from '../ui/ui/context-menu';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import echo from '../utils/echo';

/**
 * Le statut agrégé d'un dossier, façon feu tricolore — même règle de
 * priorité que toneDossier() (partagée avec OpenFolder.jsx), juste
 * alimentée par les compteurs déjà renvoyés par l'API catégories.
 */
function getFolderBadgeTone(dossier) {
  return toneDossier({
    enAttente: dossier.documents_attention_count ?? 0,
    enCours: dossier.documents_en_cours_count ?? 0,
    traites: dossier.documents_traites_count ?? 0,
  });
}

/**
 * Une case dossier, en grille (carte) ou en liste (ligne à filet fin) — même
 * bascule que sur les pages de dossiers (voir ViewToggleButtons.jsx). Le
 * menu contextuel (clic droit), le menu "⋮" tactile et la boîte de
 * renommage restent identiques quelle que soit la vue : seule la carte
 * cliquable elle-même change de forme.
 */
function FolderTile({
  dossier, vue, canManage, hauteurClasse,
  onDownload, onRename, onDelete, onEditChange, onEditSubmit, onToggleFavori, onPartager, onToggleVerrouille, onInfos,
  selectionActive, selected, onToggleSelect,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const tone = getFolderBadgeTone(dossier);
  const count = dossier.document_archives_count ?? 0;
  const nbSousDossiers = dossier.sous_dossiers_count ?? 0;
  // "25 dossiers · 14 documents" : le seul nombre de documents laissait croire
  // qu'un dossier bien structuré mais encore peu rempli était presque vide.
  const resume = nbSousDossiers > 0
    ? `${t('home.nSousDossiers', { count: nbSousDossiers })} · ${t('home.nDocuments', { count })}`
    : t('home.nDocuments', { count });
  const estVerrouille = dossier.verrouille_par_utilisateur_id != null;
  const nom = nomCategorie(dossier, i18n.resolvedLanguage);
  const clicTuile = (e) => {
    if (selectionActive) {
      e.preventDefault();
      onToggleSelect();
    }
  };
  const caseSelection = selectionActive && (
    <span
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSelect(); }}
      className={`absolute top-1.5 left-1.5 z-10 flex items-center justify-center w-5 h-5 rounded-md border-2 transition-colors ${selected ? 'bg-primary border-primary' : 'bg-card border-border'}`}
    >
      {selected && <LuCheck size={12} className='text-white' />}
    </span>
  );
  return (
    <div className='relative group'>
      <ContextMenu>
        <ContextMenuTrigger>
          {vue === 'grid' ? (
            <Link
              to={'folder/' + dossier.id}
              onClick={clicTuile}
              className={`relative flex flex-col gap-2.5 ${hauteurClasse} overflow-hidden rounded-2xl border border-border border-l-4 ${tone.bordure} bg-card p-4 text-left hover:shadow-md transition-all duration-200`}
            >
              {caseSelection}
              {/* pr-6 : dégage la place du bouton "⋮" (menu contextuel tactile),
                  positionné en absolu dans ce même coin — sans ça le badge
                  passe dessous et le texte se fait couper par l'icône. */}
              <div className='flex items-start justify-between gap-2 pr-6'>
                <span className='flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0'>
                  <LuFolder size={17} strokeWidth={1.75} />
                </span>
                <span className='text-[11px] font-semibold text-muted-foreground bg-muted rounded-full px-2 py-0.5 min-w-0 truncate' title={resume}>
                  {resume}
                </span>
              </div>
              <span className='flex items-center gap-1.5 text-sm font-semibold text-foreground'>
                {estVerrouille && <LuLock size={12} className='text-destructive shrink-0' />}
                {dossier.is_favorite && <LuPin size={12} className='text-accent shrink-0' />}
                <span className='line-clamp-3'>{nom}</span>
              </span>
              <span className={`flex items-center gap-1.5 text-[11px] font-semibold ${tone.texte}`}>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tone.point}`} />
                <span className='truncate'>{tone.label}</span>
              </span>
            </Link>
          ) : (
            <Link
              to={'folder/' + dossier.id}
              onClick={clicTuile}
              className='relative flex items-center gap-3 pr-11 pl-3.5 py-3 hover:bg-muted/40 transition-colors'
            >
              {selectionActive && <span className='shrink-0'>{caseSelection}</span>}
              <span className={`flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0 ${selectionActive ? 'ml-5' : ''}`}>
                <LuFolder size={16} strokeWidth={1.75} />
              </span>
              <div className='min-w-0 flex-1'>
                <p className='flex items-center gap-1.5 text-sm font-medium text-foreground truncate'>
                  {estVerrouille && <LuLock size={12} className='text-destructive shrink-0' />}
                  {dossier.is_favorite && <LuPin size={12} className='text-accent shrink-0' />}
                  <span className='truncate'>{nom}</span>
                </p>
                <p className={`text-xs mt-0.5 flex items-center gap-1.5 ${tone.texte}`}>
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${tone.point}`} />
                  <span className='truncate'>{resume} · {tone.label}</span>
                </p>
              </div>
            </Link>
          )}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-64">
          <ContextMenuItem inset className="cursor-pointer" onClick={() => navigate('folder/' + dossier.id)}>
            {t('home.ouvrirDossier')}
            <ContextMenuShortcut><LuBookOpen /></ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem inset className="cursor-pointer" onClick={onInfos}>
            {t('home.informations')}
            <ContextMenuShortcut><LuInfo /></ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem inset className="cursor-pointer" onClick={onDownload}>
            {t('dossierToolbar.telechargerDossier')}
            <ContextMenuShortcut><LuDownload /></ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem inset className="cursor-pointer" onClick={onToggleFavori}>
            {dossier.is_favorite ? t('dossierToolbar.retirerFavoris') : t('home.epinglerEnFavori')}
            <ContextMenuShortcut>{dossier.is_favorite ? <LuPinOff /> : <LuPin />}</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem inset className="cursor-pointer" onClick={onPartager}>
            {t('dossierToolbar.partagerDossier')}
            <ContextMenuShortcut><LuShare2 /></ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem inset className="cursor-pointer" onClick={onToggleVerrouille} disabled={!canManage}>
            {estVerrouille ? t('dossierToolbar.deverrouillerDossier') : t('dossierToolbar.verrouillerDossier')}
            <ContextMenuShortcut>{estVerrouille ? <LuUnlock /> : <LuLock />}</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem inset className="cursor-pointer" onClick={onRename} disabled={!canManage || estVerrouille}>
            {t('home.renommerDossier')}
            <ContextMenuShortcut><LuFileEdit /></ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem disabled={!canManage || estVerrouille} onClick={onDelete} inset className="text-destructive hover:text-destructive hover:bg-destructive/10 cursor-pointer">
            <div>
              {t('home.supprimerDossier')}
            </div>
            <ContextMenuShortcut><LuTrash2 /></ContextMenuShortcut>
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Menu d'actions toujours accessible (le clic-droit ne fonctionne pas au toucher) */}
      <div className='dropdown dropdown-end absolute top-1.5 right-1.5 z-10'>
        <button
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          className='flex items-center justify-center w-7 h-7 rounded-lg bg-card/90 text-muted-foreground opacity-70 hover:opacity-100 hover:bg-muted hover:text-foreground transition-all'
        >
          <LuMoreVertical size={14} />
        </button>
        <div tabIndex={0} className='dropdown-content flex items-center gap-1 bg-card border border-border rounded-xl z-20 p-1.5 shadow-lg mt-1'>
          <Link to={'folder/' + dossier.id} title={t('home.ouvrirDossier')} className='flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors'>
            <LuBookOpen size={15} />
          </Link>
          <button title={t('home.informations')} onClick={onInfos} className='flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors'>
            <LuInfo size={15} />
          </button>
          <button title={t('dossierToolbar.partagerDossier')} onClick={onPartager} className='flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors'>
            <LuShare2 size={15} />
          </button>
          <button title={estVerrouille ? t('dossierToolbar.deverrouillerDossier') : t('dossierToolbar.verrouillerDossier')} disabled={!canManage} onClick={onToggleVerrouille} className='flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors'>
            {estVerrouille ? <LuUnlock size={15} /> : <LuLock size={15} />}
          </button>
          <button title={t('home.renommerDossier')} disabled={!canManage || estVerrouille} onClick={onRename} className='flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors'>
            <LuFileEdit size={15} />
          </button>
          <button
            title={t('home.supprimerDossier')}
            disabled={!canManage || estVerrouille}
            onClick={onDelete}
            className='flex items-center justify-center w-8 h-8 rounded-lg text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
          >
            <LuTrash2 size={15} />
          </button>
        </div>
      </div>

      <dialog id={"edit_folder" + dossier.id} className="modal">
        <div className="modal-box rounded-2xl">
          <form method="dialog" className='flex justify-end'>
            <button className="btn btn-sm btn-ghost btn-circle">✕</button>
          </form>
          <h1 className='text-lg font-semibold mb-4'>
            {t('home.modifierNomDossier', { nom })}
          </h1>
          <form onSubmit={onEditSubmit}>
            <div className="mb-4">
              <label htmlFor={"name" + dossier.id} className='block text-sm font-medium mb-1.5'>{t('home.nomDuDossier')}</label>
              <input
                type="text"
                id={"name" + dossier.id}
                name='label'
                onChange={onEditChange}
                placeholder={nom}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
            <div className="modal-action">
              <button className='inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 transition-colors'>{t('categoriesSettings.modifier')}</button>
            </div>
          </form>
        </div>
      </dialog>
    </div>
  );
}

/**
 * Une carte de statistique du tableau de bord, glissable pour réordonner
 * (voir DndContext dans Home()). `PointerSensor` avec un délai d'activation
 * (rester appuyé ~400ms sans bouger) laisse un simple tap déclencher la
 * navigation normale (Link) ET laisse le défilement tactile normal se
 * produire sans déclencher un glissement accidentel — un simple seuil de
 * distance (l'ancien réglage) suffisait sur ordinateur mais entrait en
 * conflit avec le défilement au doigt sur mobile. Pas besoin d'une poignée
 * séparée : le survol/l'état "en cours de déplacement" existant (isDragging,
 * voir plus bas) indique déjà visuellement qu'on est en train de le bouger.
 *
 * `s.apercu` (facultatif) affiche un aperçu au survol des documents
 * concrets derrière le chiffre — un simple total sans détail n'aide pas
 * à savoir QUOI traiter sans cliquer et changer de page. `s.onClick` gère
 * le clic pour une carte qui n'a pas de page dédiée (ex: "À traiter", qui
 * fait défiler jusqu'au panneau détaillé plutôt que de naviguer).
 */
function CarteStat({ s, t, editable, onMasquer }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const [survole, setSurvole] = useState(false);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging || survole ? 20 : undefined,
  };
  const Wrapper = s.to ? Link : s.onClick ? 'button' : 'div';
  const wrapperProps = s.to ? { to: s.to } : s.onClick ? { type: 'button', onClick: s.onClick } : {};
  const aDesElements = s.apercu?.length > 0;
  return (
    <div
      className='relative h-full'
      onMouseEnter={() => aDesElements && setSurvole(true)}
      onMouseLeave={() => setSurvole(false)}
    >
      <Wrapper
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        {...wrapperProps}
        className='relative flex items-center gap-3 w-full h-full rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary/30 cursor-grab active:cursor-grabbing touch-manipulation select-none'
      >
        <div className={`flex items-center gap-3 w-full ${editable && !isDragging ? 'animate-tremble-carte' : ''}`}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-xl shrink-0 ${s.tint}`}>
            <s.icon size={18} />
          </div>
          <div className='text-left'>
            <p className='text-lg font-semibold text-foreground leading-none'>{s.value}</p>
            <p className='text-xs text-muted-foreground mt-0.5'>{s.label}</p>
            {s.sousLabel && <p className='text-[11px] text-muted-foreground/80 mt-0.5'>{s.sousLabel}</p>}
          </div>
        </div>
      </Wrapper>

      {editable && (
        <button
          type='button'
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMasquer(s.id); }}
          className='absolute -top-1.5 -right-1.5 z-10 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center shadow'
          title={t('home.retirerCarte')}
        >
          <IoClose size={12} />
        </button>
      )}

      {survole && aDesElements && (
        <div className='absolute left-0 top-[calc(100%+6px)] z-30 w-72 rounded-xl border border-border bg-card shadow-xl overflow-hidden animate-wizard-rise-in'>
          <div className='flex flex-col gap-0.5 p-1.5'>
            {s.apercu.map((d) => {
              const { icon: Icon, tint } = getFileTypeVisual(d.chemin_stockage_serveur || d.extension);
              return (
                <Link
                  key={d.id}
                  to={`/view/${d.id}/${d.extension || 'pdf'}`}
                  className='flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-muted/60 transition-colors'
                >
                  <span className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${tint}`}>
                    <Icon size={13} />
                  </span>
                  <span className='min-w-0 flex-1'>
                    <span className='block text-xs font-medium truncate'>{d.titre}</span>
                    <span className='block text-[11px] text-muted-foreground truncate'>{d.sousTexte}</span>
                  </span>
                </Link>
              );
            })}
          </div>
          {s.onClick && (
            <button
              type='button'
              onClick={s.onClick}
              className='block w-full text-center text-xs font-medium text-primary hover:underline px-2.5 py-2 border-t border-border'
            >
              {t('home.apercuVoirTout')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Home() {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const { hasPermission, isAdministrator } = usePermissions();
  const canManageDossiers = isAdministrator || hasPermission('gerer_categories');
  // Même garde que Sidebar.jsx/FluctuationRh.jsx : la lecture elle-même est
  // réservée, pas seulement la modification (données de santé/rémunération).
  const peutVoirFluctuationRh = isAdministrator || hasPermission('gerer_fluctuation_rh');
  const [dossiers, setDossiers] = useState([]);
  const [tousLesDocuments, setTousLesDocuments] = useState([]);
  const [folderData, setFolderData] = useState({ label: '' });
  const [searchValue, setSearchValue] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [documentsTrouves, setDocumentsTrouves] = useState([]);
  const [rechercheEnCours, setRechercheEnCours] = useState(false);
  const [user, setUser] = useState();
  const [aTraiter, setATraiter] = useState({ en_attente: [], a_purger: [], echeance_traitement: [] });
  const [paiCompteurs, setPaiCompteurs] = useState({ dossiers_actifs: 0, objectifs_en_retard: 0 });
  const [courrierCompteurs, setCourrierCompteurs] = useState({ en_attente: 0 });
  const [appelsCompteurs, setAppelsCompteurs] = useState({ a_traiter: 0 });
  const [chequesCompteurs, setChequesCompteurs] = useState({ a_traiter: 0 });
  const [fluctuationRhCompteurs, setFluctuationRhCompteurs] = useState({ renouvellements_essai: 0 });
  const [showATraiter, setShowATraiter] = useState(true);
  const [view, setView] = useState('grid');
  const [tri, setTri] = useState('nom');
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [shareFolder, setShareFolder] = useState(null);
  const [densite, setDensite] = useState('normal');
  const [masquerVides, setMasquerVides] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [infoDossier, setInfoDossier] = useState(null);
  // Cible du clic sur la carte "À traiter" — fait défiler jusqu'au panneau
  // détaillé plutôt que de naviguer nulle part (bouton qui ne faisait rien
  // jusqu'ici, retour utilisateur).
  const aTraiterSectionRef = useRef(null);

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectMode() {
    setSelectMode((v) => !v);
    setSelectedIds(new Set());
  }

  function deleteFolder(id){
    deleteCategorieById(id).then(function(res){
      if (res.status===200) {
        fetchFolders()
        toast.success(t('home.dossierSupprime'))
      }
    })
  }

  async function confirmDeleteFolder(dossier){
    if (await confirm({ message: t('home.confirmerSuppressionDossier', { nom: nomCategorie(dossier, i18n.resolvedLanguage) }), danger: true })) {
      deleteFolder(dossier.id)
    }
  }

  function demanderTelechargement(id){
    downloadCategorie(id).then(async (res) => {
      if (res.status === 202) {
        toast.success(t('home.preparationDossier'))
      } else {
        const data = await res.json()
        toast.error(data?.error || t('home.telechargementEchoue'))
      }
    }).catch(() => toast.error(t('commun.erreurGenerique')))
  }

  function toggleFavori(dossier) {
    const appel = dossier.is_favorite ? defavoriCategorie : favoriCategorie;
    appel(dossier.id).then((res) => {
      if (res.status === 200) fetchFolders();
      else toast.error(t('commun.erreurGenerique'));
    }).catch(() => toast.error(t('commun.erreurGenerique')));
  }

  function toggleVerrouille(dossier) {
    const estVerrouille = dossier.verrouille_par_utilisateur_id != null;
    const appel = estVerrouille ? deverrouillerCategorie : verrouillerCategorie;
    appel(dossier.id).then(async (res) => {
      if (res.status === 200) {
        toast.success(estVerrouille ? t('home.dossierDeverrouille') : t('home.dossierVerrouille'));
        fetchFolders();
      } else {
        toast.error(t('commun.erreurGenerique'));
      }
    }).catch(() => toast.error(t('commun.erreurGenerique')));
  }

  /** Construit les infos affichées par InfoDossierModal — aucun appel réseau, tout est déjà en mémoire. */
  function infosPourDossier(dossier) {
    return {
      label: nomCategorie(dossier, i18n.resolvedLanguage),
      total: dossier.document_archives_count ?? 0,
      // Même définition que la carte "À traiter" du tableau de bord : rejetés/expirés + pas encore validés.
      attention: (dossier.documents_attention_count ?? 0) + (dossier.documents_en_cours_count ?? 0),
      traites: dossier.documents_traites_count ?? 0,
      sousDossiers: dossier.sous_dossiers_count ?? null,
      creeLe: dossier.created_at,
      verrouille: dossier.verrouille_par_utilisateur_id != null,
    };
  }

  function updateFolder(e,id){
    e.preventDefault()
    updateCatgory(folderData,id).then(async function(res){
      if (res.status ===200) {
        fetchFolders()
        document.getElementById('edit_folder' + id)?.close()
      }
      else{
        toast.error(t('commun.erreurGenerique'))
      }
    }).catch(function(err){
      console.log(err)
      toast.error(t('commun.erreurGenerique'))
    })
  }

  const fetchFolders = async () => {
    try {
      const res = await getCategorie();
      if (res.status === 200) {
        const data = await res.json();
        setDossiers(data);
        setSearchValue(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTousLesDocuments = async () => {
    try {
      const res = await getDocument();
      if (res.status === 200) {
        setTousLesDocuments(await res.json());
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchATraiter = async () => {
    try {
      const res = await getDocumentsATraiter();
      if (res.status === 200) {
        setATraiter(await res.json());
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPaiCompteurs = async () => {
    try {
      const res = await getPaiCompteurs();
      if (res.status === 200) {
        setPaiCompteurs(await res.json());
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCourrierCompteurs = async () => {
    try {
      const res = await getCourrierCompteurs();
      if (res.status === 200) {
        setCourrierCompteurs(await res.json());
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Contrairement aux 4 autres compteurs (ouverts à tout le personnel), celui-ci
  // reste réservé à gerer_fluctuation_rh côté serveur (données de santé/
  // rémunération) — appelé seulement si peutVoirFluctuationRh, voir plus bas.
  const fetchFluctuationRhCompteurs = async () => {
    try {
      const res = await getCompteursFluctuationsRh();
      if (res.status === 200) {
        setFluctuationRhCompteurs(await res.json());
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchAppelsCompteurs = async () => {
    try {
      const res = await getAppelsCompteurs();
      if (res.status === 200) {
        setAppelsCompteurs(await res.json());
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchChequesCompteurs = async () => {
    try {
      const res = await getChequesCompteurs();
      if (res.status === 200) {
        setChequesCompteurs(await res.json());
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getFormData = (e, callback) => {
    callback(prevData => ({
      ...prevData,
      [e.target.name]: e.target.value
    }));
  };

  function searchFolder(e) {
    e.preventDefault();
    const value = e.target.value;
    setSearchTerm(value);

    if (value === "") {
      setSearchValue(dossiers);
      setDocumentsTrouves([]);
      return;
    }

    // Les dossiers restent filtrés côté client (liste courte, déjà chargée) —
    // seuls les documents passent par la recherche serveur ci-dessous
    // (voir l'effet debounce sur searchTerm), qui interroge un vrai index
    // plein texte plutôt que la liste déjà chargée en mémoire.
    setSearchValue(filtrerParRequete(dossiers, (d) => [d.libelle_cat, d.libelle_cat_en], value, { trierParPertinence: true }));
  }

  // Recherche serveur (plein texte, voir DocumentController::recherche()) pour
  // les documents — remplace l'ancien filtrage client sur tousLesDocuments,
  // qui ne portait jamais sur le texte des PDF/images non scannés (seul le
  // scan caméra alimentait texte_extrait). Débounce pour ne pas interroger le
  // serveur à chaque frappe.
  useEffect(() => {
    if (searchTerm === '') { setDocumentsTrouves([]); setRechercheEnCours(false); return; }
    setRechercheEnCours(true);
    const minuteur = setTimeout(() => {
      rechercheDocuments(searchTerm).then(async (res) => {
        if (res.status === 200) setDocumentsTrouves(await res.json());
      }).catch((error) => console.log(error)).finally(() => setRechercheEnCours(false));
    }, 300);
    return () => clearTimeout(minuteur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const createFolder = async (e) => {
    e.preventDefault();
    try {
      const res = await createCategorie(folderData);
      if (res.status === 201) {
        fetchFolders();
        toast.success(t('home.dossierCree'));
        setFolderData({ label: '' });
        document.getElementById('createFolder').close();
      } else {
        toast.error(t('commun.erreurGenerique'));
      }
    } catch (error) {
      toast.error(t('commun.erreurGenerique'));
      console.log(error);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    setUser(JSON.parse(sessionStorage.getItem("user")));
    fetchFolders();
    fetchTousLesDocuments();
    fetchATraiter();
    // PAI/Courriers/Appels/Chèques : compteurs visibles par tout le personnel
    // interne en lecture (les routes compteurs ne sont plus permission-gated
    // côté serveur non plus, voir routes/api.php) — seules les actions
    // d'écriture à l'intérieur de chaque page restent réservées.
    fetchPaiCompteurs();
    fetchCourrierCompteurs();
    fetchAppelsCompteurs();
    fetchChequesCompteurs();
    if (peutVoirFluctuationRh) fetchFluctuationRhCompteurs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suppression ou changement de statut sur N'IMPORTE QUEL document pendant
  // qu'on a le tableau de bord ouvert : les compteurs par dossier (pastille
  // rouge/verte) et les rappels "à traiter" se remettent à jour tout seuls,
  // sans bouton actualiser (voir le canal global "documents", diffusé par
  // DocumentController::destroy()/DocumentStatusService::transitionTo()).
  useEffect(() => {
    const channel = echo.channel('documents');
    const rafraichir = () => { fetchFolders(); fetchATraiter(); };
    channel.listen('.document.supprime', rafraichir);
    channel.listen('.statut.maj', rafraichir);
    return () => echo.leave('documents');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function joursDepuis(dateStr) {
    const jours = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
    return jours;
  }

  function joursAvant(dateStr) {
    return Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }

  function saluation() {
    const heure = new Date().getHours();
    return heure < 18 ? t('home.bonjour') : t('home.bonsoir');
  }

  const totalAttention = dossiers.reduce((sum, d) => sum + (d.documents_attention_count ?? 0), 0);
  const totalEnCours = dossiers.reduce((sum, d) => sum + (d.documents_en_cours_count ?? 0), 0);
  const totalTraites = dossiers.reduce((sum, d) => sum + (d.documents_traites_count ?? 0), 0);
  const totalSousDossiers = dossiers.reduce((sum, d) => sum + (d.sous_dossiers_count ?? 0), 0);
  // "À traiter" = tout ce qui n'est pas encore validé/archivé : rejetés/expirés
  // (groupe "attention") ET soumis/transmis/en cours (groupe "en_cours"). Ne
  // compter que le premier affichait "0 À traiter" et "Tout est à jour" alors
  // que plus d'une centaine de documents attendaient une validation.
  const totalATraiter = totalAttention + totalEnCours;
  const courriersEnAttente = courrierCompteurs.en_attente ?? 0;

  // Aperçu au survol de la carte "À traiter" (voir CarteStat) — mêmes
  // documents et le même texte que le panneau détaillé plus bas, juste
  // condensés en 5.
  const apercuATraiter = [
    ...aTraiter.en_attente.map((d) => ({ ...d, sousTexte: t('home.enAttenteDepuis', { count: joursDepuis(d.depuis) }) })),
    ...(aTraiter.echeance_traitement || []).map((d) => {
      const jours = joursAvant(d.echeance);
      return { ...d, sousTexte: jours >= 0 ? t('home.aTraiterDans', { count: jours }) : t('home.delaiDepasseDepuis', { count: -jours }) };
    }),
  ].slice(0, 5);

  function ouvrirEtDeplierATraiter() {
    setShowATraiter(true);
    requestAnimationFrame(() => aTraiterSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  const stats = [
    // bg-primary/10 (--primary est un bleu marine sombre, 215 55% 24%) reste
    // trop proche de bg-muted (gris neutre bleuté, 210 20% 95%) une fois
    // réduit en tinte pâle — au point de se confondre avec l'état "neutre" de
    // la carte PAI en retard juste en dessous. Même logique que "Documents"
    // ci-dessous : couleur Tailwind franche plutôt qu'un token du thème trop
    // proche du gris pour cet usage précis.
    {
      id: 'dossiers',
      label: t('home.dossiers'),
      value: dossiers.length,
      // Les dossiers de service seuls (8) masquaient l'arborescence réelle.
      sousLabel: totalSousDossiers > 0 ? `+ ${t('home.nSousDossiers', { count: totalSousDossiers })}` : null,
      icon: LuFolder,
      tint: 'bg-blue-500/10 text-blue-600',
    },
    // bg-secondary/text-secondary donnait quasiment la même couleur que
    // "Dossiers" (bg-primary) : --primary (#1B365D) et --secondary (#274559)
    // sont deux bleus marine très proches, indiscernables une fois passés en
    // tinte à 10% d'opacité — violet choisi pour rester net à côté des autres
    // couleurs déjà prises par les cartes voisines (rouge, vert, or).
    { id: 'documents', label: t('sidebar.documents'), value: tousLesDocuments.length, icon: LuFileText, tint: 'bg-purple-500/10 text-purple-600' },
    {
      id: 'a_traiter',
      label: t('dossierToolbar.aTraiter'),
      value: totalATraiter,
      icon: LuAlertCircle,
      tint: 'bg-destructive/10 text-destructive',
      onClick: ouvrirEtDeplierATraiter,
      apercu: apercuATraiter,
    },
    { id: 'traites', label: t('dossierToolbar.traites'), value: totalTraites, icon: LuCheckCircle2, tint: 'bg-green-500/10 text-green-600' },
  ];
  // PAI/Courriers/Appels/Chèques : visibles par tout le personnel interne
  // (demande explicite — lecture ouverte à tous, seules les actions
  // d'écriture à l'intérieur de chaque page restent réservées par permission).
  stats.push({
    id: 'pai',
    label: t('home.paiEnRetard'),
    value: paiCompteurs.objectifs_en_retard,
    icon: LuListChecks,
    // Orange, pas destructive (rouge) : sinon indiscernable de la carte
    // "À traiter" juste au-dessus, qui utilise déjà ce rouge en permanence.
    tint: paiCompteurs.objectifs_en_retard > 0 ? 'bg-orange-500/10 text-orange-600' : 'bg-muted text-muted-foreground',
    to: '/pai',
  });
  stats.push({
    id: 'courriers',
    label: t('home.courriersEnAttente'),
    value: courrierCompteurs.en_attente,
    icon: LuMail,
    tint: courrierCompteurs.en_attente > 0 ? 'bg-accent/20 text-accent-foreground' : 'bg-muted text-muted-foreground',
  });
  stats.push({
    id: 'appels',
    label: t('home.appelsATraiter'),
    value: appelsCompteurs.a_traiter,
    icon: LuPhoneIncoming,
    // Sarcelle (teal), pas rose : à faible opacité, rose restait trop proche
    // du rouge de "À traiter" pour vraiment s'en distinguer d'un coup d'œil
    // (retour utilisateur) — teal est loin sur le cercle chromatique de
    // toutes les autres teintes déjà prises (rouge, orange, or, vert, bleu, violet).
    tint: appelsCompteurs.a_traiter > 0 ? 'bg-teal-500/10 text-teal-600' : 'bg-muted text-muted-foreground',
    to: '/appels',
  });
  stats.push({
    id: 'cheques',
    label: t('home.chequesATraiter'),
    value: chequesCompteurs.a_traiter,
    icon: LuLandmark,
    // Indigo : encore une teinte non prise par les cartes voisines (rouge,
    // orange, or, vert, bleu, violet, sarcelle).
    tint: chequesCompteurs.a_traiter > 0 ? 'bg-indigo-500/10 text-indigo-600' : 'bg-muted text-muted-foreground',
    to: '/cheques',
  });
  // Réservée à qui voit la Fluctuation RH (donnée de santé/rémunération,
  // voir peutVoirFluctuationRh plus haut) — contrairement aux 4 cartes
  // précédentes, pas ouverte à tout le personnel.
  if (peutVoirFluctuationRh) {
    stats.push({
      id: 'essais',
      label: t('home.renouvellementsEssai'),
      value: fluctuationRhCompteurs.renouvellements_essai,
      icon: LuCalendarClock,
      // Rose : dernière teinte encore disponible (rouge, orange, or, vert,
      // bleu, violet, sarcelle, indigo déjà prises par les cartes voisines).
      tint: fluctuationRhCompteurs.renouvellements_essai > 0 ? 'bg-rose-500/10 text-rose-600' : 'bg-muted text-muted-foreground',
      to: '/fluctuation-rh',
    });
  }

  // Ordre des cartes mémorisé par appareil — voir useOrdrePersonnalise().
  const [ordreWidgets, setOrdreWidgets] = useOrdrePersonnalise('his_ordre_widgets_tableau_bord', stats.map((s) => s.id));
  const statsTriees = ordreWidgets.map((id) => stats.find((s) => s.id === id)).filter(Boolean);

  // Cartes retirées par la personne (bouton ⚙ ci-dessous) — même principe que
  // l'ordre : mémorisé par appareil, jamais synchronisé entre appareils.
  const [cartesMasquees, setCartesMasqueesEtat] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem('his_cartes_masquees_tableau_bord') || '[]');
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  });
  const [editionCartes, setEditionCartes] = useState(false);
  function setCartesMasquees(nouvelles) {
    setCartesMasqueesEtat(nouvelles);
    try { localStorage.setItem('his_cartes_masquees_tableau_bord', JSON.stringify(nouvelles)); } catch { /* stockage indisponible, tant pis */ }
  }
  const statsVisibles = statsTriees.filter((s) => !cartesMasquees.includes(s.id));
  const statsCachees = statsTriees.filter((s) => cartesMasquees.includes(s.id));
  // delay+tolerance (rester appuyé sans trop bouger) plutôt qu'un simple
  // seuil de distance : sur mobile, un léger mouvement du doigt suffisait à
  // déclencher un glissement à la place du défilement normal de la page —
  // voir le commentaire de CarteStat plus bas.
  // MouseSensor + TouchSensor séparés (pas PointerSensor) — le tactile a
  // besoin de son propre capteur : PointerSensor exige touch-action:none
  // pour marcher de façon fiable (voir doc dnd-kit), ce qui bloque le
  // défilement en permanence quel que soit le délai. TouchSensor gère ça en
  // JS (preventDefault() seulement une fois le glissement réellement activé)
  // et se contente de touch-action:manipulation, qui laisse le défilement
  // tactile normal fonctionner tant que le délai n'est pas écoulé.
  const capteursWidgets = useSensors(
    useSensor(MouseSensor, { activationConstraint: { delay: 400, tolerance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 400, tolerance: 6 } }),
  );
  function onDragEndWidgets(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ancienIndex = ordreWidgets.indexOf(active.id);
    const nouvelIndex = ordreWidgets.indexOf(over.id);
    setOrdreWidgets(arrayMove(ordreWidgets, ancienIndex, nouvelIndex));
  }

  const favoris = dossiers.filter((d) => d.is_favorite);

  /** Même ordre de priorité que toneDossier() : à traiter d'abord, jusqu'à "aucun document". */
  function groupeDossier(dossier) {
    if ((dossier.documents_attention_count ?? 0) > 0) return 'attention';
    if ((dossier.documents_en_cours_count ?? 0) > 0) return 'en_cours';
    if ((dossier.documents_traites_count ?? 0) > 0) return 'traite';
    return 'aucun';
  }
  const RANG_GROUPE = { attention: 0, en_cours: 1, traite: 2, aucun: 3 };

  const dossiersAffiches = searchValue
    .filter((d) => filtreStatut === 'tous' || groupeDossier(d) === filtreStatut)
    .filter((d) => !masquerVides || (d.document_archives_count ?? 0) > 0)
    .slice()
    .sort((a, b) => {
      if (tri === 'documents') return (b.document_archives_count ?? 0) - (a.document_archives_count ?? 0);
      if (tri === 'statut') return RANG_GROUPE[groupeDossier(a)] - RANG_GROUPE[groupeDossier(b)];
      return (a.libelle_cat || '').localeCompare(b.libelle_cat || '');
    });

  return (
    <div className='flex flex-col flex-grow py-6 gap-1'>
      <Breadcrumbs where={t('sidebar.tableauDeBord')} />
      <div className='mb-6 mt-1'>
        <div className='flex items-start justify-between flex-wrap gap-3 mb-4'>
          <div>
            <h2 className='text-2xl font-semibold text-foreground'>{saluation()}, {getDisplayName(user) || t('home.bienvenue')}</h2>
            <p className='text-sm text-muted-foreground mt-1'>
              {totalATraiter > 0
                ? t('home.documentsVousAttendent', { count: totalATraiter })
                : courriersEnAttente > 0
                  ? t('home.courriersVousAttendent', { count: courriersEnAttente })
                  : t('home.toutEstAJour')}
            </p>
          </div>
          <p className='text-sm text-muted-foreground capitalize sm:text-right'>
            {new Date().toLocaleDateString(i18n.resolvedLanguage, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {showATraiter && (aTraiter.en_attente.length > 0 || aTraiter.a_purger.length > 0 || (aTraiter.echeance_traitement?.length > 0)) && (
          <div ref={aTraiterSectionRef} className='rounded-2xl border border-accent/40 bg-accent/5 p-4 mb-4'>
            <div className='flex items-center justify-between mb-3'>
              <h3 className='text-sm font-semibold text-foreground'>{t('home.aTraiterBientot')}</h3>
              <button onClick={() => setShowATraiter(false)} className='text-muted-foreground hover:text-foreground transition-colors'>
                <IoClose size={18} />
              </button>
            </div>
            <div className='flex flex-col gap-2'>
              {aTraiter.en_attente.map((d) => (
                <Link key={`att-${d.id}`} to={`/view/${d.id}/${d.extension || 'pdf'}`} className='flex items-center gap-2.5 text-sm hover:bg-card rounded-lg p-1.5 -m-1.5 transition-colors'>
                  <span className='flex items-center justify-center w-8 h-8 rounded-lg bg-accent/20 text-accent-foreground shrink-0'>
                    <LuClock size={15} />
                  </span>
                  <span className='min-w-0 truncate'>
                    <span className='font-medium'>{d.titre}</span>
                    <span className='text-muted-foreground'> — {t('home.enAttenteDepuis', { count: joursDepuis(d.depuis) })}</span>
                  </span>
                </Link>
              ))}
              {aTraiter.a_purger.map((d) => {
                const jours = joursAvant(d.echeance);
                return (
                  <Link key={`pur-${d.id}`} to={`/view/${d.id}/${d.extension || 'pdf'}`} className='flex items-center gap-2.5 text-sm hover:bg-card rounded-lg p-1.5 -m-1.5 transition-colors'>
                    <span className='flex items-center justify-center w-8 h-8 rounded-lg bg-destructive/10 text-destructive shrink-0'>
                      <LuArchive size={15} />
                    </span>
                    <span className='min-w-0 truncate'>
                      <span className='font-medium'>{d.titre}</span>
                      <span className='text-muted-foreground'> — {jours >= 0 ? t('home.aPurgerDans', { count: jours }) : t('home.echeanceDepasseeDepuis', { count: -jours })}</span>
                    </span>
                  </Link>
                );
              })}
              {(aTraiter.echeance_traitement || []).map((d) => {
                const jours = joursAvant(d.echeance);
                return (
                  <Link key={`ech-${d.id}`} to={`/view/${d.id}/${d.extension || 'pdf'}`} className='flex items-center gap-2.5 text-sm hover:bg-card rounded-lg p-1.5 -m-1.5 transition-colors'>
                    <span className={`flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${d.depassee ? 'bg-destructive/10 text-destructive' : 'bg-accent/20 text-accent-foreground'}`}>
                      <LuCalendarClock size={15} />
                    </span>
                    <span className='min-w-0 truncate'>
                      <span className='font-medium'>{d.titre}</span>
                      <span className='text-muted-foreground'> — {jours >= 0 ? t('home.aTraiterDans', { count: jours }) : t('home.delaiDepasseDepuis', { count: -jours })}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <div className='flex justify-end mb-2'>
          <button
            type='button'
            onClick={() => setEditionCartes((v) => !v)}
            title={editionCartes ? t('home.terminerPersonnalisation') : t('home.personnaliserCartes')}
            className={`flex items-center justify-center w-7 h-7 rounded-full border transition-colors ${editionCartes ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/40'}`}
          >
            {editionCartes ? <LuCheck size={14} /> : <LuSlidersHorizontal size={13} />}
          </button>
        </div>

        <DndContext sensors={capteursWidgets} collisionDetection={closestCenter} onDragEnd={onDragEndWidgets}>
          <SortableContext items={ordreWidgets} strategy={rectSortingStrategy}>
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
              {statsVisibles.map((s) => (
                <CarteStat
                  key={s.id}
                  s={s}
                  t={t}
                  editable={editionCartes}
                  onMasquer={(id) => setCartesMasquees([...cartesMasquees, id])}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {editionCartes && statsCachees.length > 0 && (
          <div className='mt-3 pt-3 border-t border-dashed border-border'>
            <p className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2'>{t('home.cartesMasquees')}</p>
            <div className='flex flex-wrap gap-2'>
              {statsCachees.map((s) => (
                <button
                  key={s.id}
                  type='button'
                  onClick={() => setCartesMasquees(cartesMasquees.filter((id) => id !== s.id))}
                  className='inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-dashed border-border bg-background px-2.5 py-1.5 text-muted-foreground hover:border-primary/50 hover:text-foreground transition-colors'
                >
                  <span className='text-primary font-bold'>+</span> {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <Cards />
      <div>
        {favoris.length > 0 && (
          <div className='flex items-center gap-2 flex-wrap mb-4'>
            <span className='text-[11px] font-semibold text-muted-foreground uppercase tracking-wide shrink-0'>{t('home.favoris')}</span>
            {favoris.map((d) => (
              <Link
                key={d.id}
                to={'folder/' + d.id}
                className='inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors'
              >
                <LuPin size={12} className='text-accent' /> {nomCategorie(d, i18n.resolvedLanguage)}
              </Link>
            ))}
          </div>
        )}
        <DossierToolbar
          searchValue={searchTerm}
          onSearchChange={searchFolder}
          searchPlaceholder={t('home.rechercherDossier')}
          actionsNouveau={[
            { label: t('home.nouveauDossier'), icon: LuFolderPlus, onClick: () => document.getElementById('createFolder').showModal() },
            { label: t('home.archiverDocument'), icon: LuUploadCloud, onClick: () => document.getElementById('archiverDocumentHome').showModal() },
          ]}
          tri={tri}
          setTri={setTri}
          optionsTri={[
            { value: 'nom', label: t('home.triNom') },
            { value: 'documents', label: t('home.triDocuments') },
            { value: 'statut', label: t('home.triStatut') },
          ]}
          filtreStatut={filtreStatut}
          setFiltreStatut={setFiltreStatut}
          masquerVides={masquerVides}
          setMasquerVides={setMasquerVides}
          densite={densite}
          setDensite={setDensite}
          onActualiser={fetchFolders}
          extra={
            <button
              onClick={toggleSelectMode}
              title={t('home.selectionMultiple')}
              className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 transition-colors ${selectMode ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
            >
              <LuCheck size={16} />
            </button>
          }
          view={view}
          setView={setView}
        />
        <BulkFolderActionBar dossiers={dossiersAffiches} selectedIds={selectedIds} onClear={() => setSelectedIds(new Set())} onChanged={fetchFolders} />
        {dossiersAffiches.length === 0 && (
          <div className='flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border py-14 text-center'>
            <LuFolderSearch size={32} className='text-muted-foreground' strokeWidth={1.5} />
            <p className='text-sm font-medium text-foreground'>
              {dossiers.length === 0 ? t('home.aucunDossier') : t('home.aucunDossierRecherche')}
            </p>
            <p className='text-xs text-muted-foreground'>
              {dossiers.length === 0 ? t('home.creezPremierDossier') : t('home.essayezAutreTerme')}
            </p>
          </div>
        )}
        {/* Le nombre de colonnes s'adapte à la densité choisie (compact/normal/grand) ;
            "normal" garde lg:4 (pas 6) pour que les noms de dossier français (souvent
            longs, ex: "Gestion bénéficiaires & secteurs") s'affichent sans déborder. */}
        <div className={view === 'grid'
          ? DENSITE_COLS[densite]
          : 'flex flex-col rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden'
        }>
          {dossiersAffiches.map((dossier, k) => (
            <FolderTile
              key={k}
              dossier={dossier}
              vue={view}
              hauteurClasse={DENSITE_HAUTEUR[densite]}
              canManage={canManageDossiers}
              onDownload={() => demanderTelechargement(dossier.id)}
              onRename={() => document.getElementById('edit_folder' + dossier.id).showModal()}
              onDelete={() => confirmDeleteFolder(dossier)}
              onEditChange={(e) => getFormData(e, setFolderData)}
              onEditSubmit={(e) => updateFolder(e, dossier.id)}
              onToggleFavori={() => toggleFavori(dossier)}
              onPartager={() => setShareFolder(dossier)}
              onToggleVerrouille={() => toggleVerrouille(dossier)}
              onInfos={() => setInfoDossier(infosPourDossier(dossier))}
              selectionActive={selectMode}
              selected={selectedIds.has(dossier.id)}
              onToggleSelect={() => toggleSelect(dossier.id)}
            />
          ))}
        </div>

        {searchTerm !== '' && (
          <div className='mt-6'>
            {documentsTrouves.length > 0 ? (
              <>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3'>
                  {t('home.documentsN', { count: documentsTrouves.length })}
                </p>
                <div className='grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-3'>
                  {documentsTrouves.map((doc) => {
                    const { icon: Icon, tint } = getFileTypeVisual(doc.chemin_stockage_serveur);
                    const ext = String(doc.chemin_stockage_serveur).split('.').pop();
                    return (
                      <button
                        key={doc.id}
                        onClick={() => navigate(`/view/${doc.id}/${ext}`)}
                        className='flex items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left hover:border-primary/40 hover:shadow-md transition-all duration-200'
                      >
                        <div className={`flex items-center justify-center w-11 h-11 rounded-xl shrink-0 ${tint}`}>
                          <Icon size={19} />
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p className='text-sm font-medium text-foreground truncate'>{doc.titre_document}</p>
                          <p className='text-xs text-muted-foreground truncate mt-0.5'>{nomCategorie(doc.categorie_document, i18n.resolvedLanguage)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : !rechercheEnCours && (
              <p className='text-sm text-muted-foreground text-center py-10'>
                {t('home.aucunDocumentTrouve', { terme: searchTerm })}
              </p>
            )}
          </div>
        )}
      </div>

      <dialog id="createFolder" className="modal">
        <div className="modal-box w-3/4 rounded-2xl">
          <form method="dialog" className='flex justify-end'>
            <button className='btn btn-sm btn-ghost btn-circle'><IoClose /></button>
          </form>
          <div className='py-2'>
            <h1 className='text-lg font-semibold mb-4'>{t('home.nouveauDossier')}</h1>
            <form onSubmit={createFolder}>
              <div className="mb-4">
                <label htmlFor="name" className='block text-sm font-medium mb-1.5'>{t('home.nomDuDossier')}</label>
                <input
                  type="text"
                  id='name'
                  name='label'
                  onChange={(e) => getFormData(e, setFolderData)}
                  placeholder="Informatique"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="name_en" className='block text-sm font-medium mb-1.5'>{t('categoriesSettings.categorieEn')}</label>
                <input
                  type="text"
                  id='name_en'
                  name='label_en'
                  onChange={(e) => getFormData(e, setFolderData)}
                  placeholder="IT"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <p className='text-xs text-muted-foreground mt-1'>{t('categoriesSettings.indicationTraduction')}</p>
              </div>
              <div className="modal-action">
                <button className='inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 transition-colors'>{t('home.creer')}</button>
              </div>
            </form>
          </div>
        </div>
      </dialog>

      <ArchiverDocumentModal dialogId='archiverDocumentHome' categories={dossiers} onArchive={() => { fetchFolders(); fetchTousLesDocuments(); fetchATraiter(); }} />

      <ShareFolderModal folder={shareFolder} isOpen={!!shareFolder} onClose={() => setShareFolder(null)} />
      <InfoDossierModal infos={infoDossier} isOpen={!!infoDossier} onClose={() => setInfoDossier(null)} />

    </div>
  );
}

export default Home;
